import { useState, useEffect } from "react";
import { Button, TextField, IconButton, Stepper, Step, StepLabel, Radio, RadioGroup, FormControlLabel, Checkbox } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import { db } from "../firebase-config";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import "../style/QuestionnaireEdit.css";
import "../style/Global.css";

function EditQuestionnaire() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [questionnaire, setQuestionnaire] = useState(null);
  const [updatedTitle, setUpdatedTitle] = useState("");
  const [updatedDescription, setUpdatedDescription] = useState("");
  const [updatedQuestions, setUpdatedQuestions] = useState([]);
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatedStartDate, setUpdatedStartDate] = useState("");
  const [updatedEndDate, setUpdatedEndDate] = useState("");
  const [studentsPerTeam, setStudentsPerTeam] = useState(3);
  const [enableFeedback, setEnableFeedback] = useState(false);
  const [enableTeamAllocationFeedback, setEnableTeamAllocationFeedback] = useState(false);
  const [teamAllocationFeedbackDuration, setTeamAllocationFeedbackDuration] = useState(7);

  useEffect(() => {
    const fetchQuestionnaire = async () => {
      try {
        const docRef = doc(db, "Questionnaires", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setQuestionnaire(data);
          setUpdatedTitle(data.title);
          setUpdatedDescription(data.description || "");
          setUpdatedStartDate(data.startDate || "");
          setUpdatedEndDate(data.endDate || "");
          setStudentsPerTeam(data.studentsPerTeam || 3);
          setEnableFeedback(data.enableFeedback || false);
          setEnableTeamAllocationFeedback(data.enableTeamAllocationFeedback || false);
          setTeamAllocationFeedbackDuration(data.teamAllocationFeedbackDuration || 7);

          const toLocalDateTime = (timestamp) => {
            if (!timestamp?.seconds) return "";
            const date = new Date(timestamp.seconds * 1000);
            const offset = date.getTimezoneOffset();
            const localDate = new Date(date.getTime() - offset * 60000);
            return localDate.toISOString().slice(0, 16); // yyyy-MM-ddTHH:mm
          };

          setUpdatedStartDate(toLocalDateTime(data.startDate));
          setUpdatedEndDate(toLocalDateTime(data.endDate));


          setUpdatedQuestions(
            data.questions?.map(q => ({
              ...q,
              options: q.options || [],
              WeightFun: q.WeightFun !== undefined ? q.WeightFun : "group",
            })) || []
          );
        } else {
          console.error("Questionnaire not found!");
        }
      } catch (error) {
        console.error("Error fetching questionnaire:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionnaire();
  }, [id]);

  const hasDuplicateOptions = updatedQuestions.some(question => {
    const nonEmptyOptions = question.options.filter(opt => opt.trim() !== "");
    const uniqueOptions = new Set(nonEmptyOptions.map(opt => opt.trim().toLowerCase()));
    return nonEmptyOptions.length !== uniqueOptions.size;
  });

  const handleSave = async () => {
    if (hasDuplicateOptions) {
      setError("Please remove duplicate options before saving.");
      return;
    }

    if (!isValidQuestionnaire()) {
      setError("Please fill in all required fields.");
      return;
    }

    try {
      const docRef = doc(db, "Questionnaires", id);
      const now = new Date();
      const start = new Date(updatedStartDate);
      const end = new Date(updatedEndDate);

      const isStartToday = (
        start.getFullYear() === now.getFullYear() &&
        start.getMonth() === now.getMonth() &&
        start.getDate() === now.getDate()
      );

      const actualStart = isStartToday ? now : start;
      const status = actualStart <= now ? "active" : "inactive";

      let teamAllocationFeedbackStart = null;
      let teamAllocationFeedbackEnd = null;
      if (enableTeamAllocationFeedback) {
        teamAllocationFeedbackStart = end;
        teamAllocationFeedbackEnd = new Date(end);
        teamAllocationFeedbackEnd.setDate(end.getDate() + teamAllocationFeedbackDuration);
      }

      await updateDoc(docRef, {
        title: updatedTitle,
        description: updatedDescription,
        startDate: actualStart,
        endDate: end,
        questions: updatedQuestions,
        studentsPerTeam,
        enableFeedback,
        enableTeamAllocationFeedback,
        teamAllocationFeedbackDuration: enableTeamAllocationFeedback ? teamAllocationFeedbackDuration : null,
        teamAllocationFeedbackStart,
        teamAllocationFeedbackEnd,
        status
      });

      navigate("/ViewQuestionnaire");
    } catch (error) {
      console.error("Error updating questionnaire:", error);
      setError("Failed to save changes. Try again.");
    }
  };

  const handleNext = () => {
    if (step === 0 && !isValidQuestionnaire()) {
      setError("Please fill out all fields and add at least one question.");
      return;
    }
    setError("");
    setStep(step + 1);
  };

  const handleBack = () => setStep(step - 1);

  const addQuestion = () => {
    setUpdatedQuestions([...updatedQuestions, { text: "", options: [] }]);
  };

  const handleQuestionChange = (index, value) => {
    const newQuestions = [...updatedQuestions];
    newQuestions[index].text = value;
    setUpdatedQuestions(newQuestions);
  };

  const handleWeightFunChange = (qIndex, value) => {
    const newQuestions = [...updatedQuestions];
    newQuestions[qIndex].WeightFun = value;
    setUpdatedQuestions(newQuestions);
  };

  const deleteQuestion = (index) => {
    const newQuestions = updatedQuestions.filter((_, i) => i !== index);
    setUpdatedQuestions(newQuestions);
  };

  const handleOptionChange = (qIndex, optIndex, value) => {
    const newQuestions = [...updatedQuestions];
    newQuestions[qIndex].options[optIndex] = value;
    setUpdatedQuestions(newQuestions);
  };

  const addOption = (qIndex) => {
    const newQuestions = [...updatedQuestions];
    newQuestions[qIndex].options.push("");
    setUpdatedQuestions(newQuestions);
  };

  const deleteOption = (qIndex, optIndex) => {
    const newQuestions = [...updatedQuestions];
    newQuestions[qIndex].options = newQuestions[qIndex].options.filter((_, i) => i !== optIndex);
    setUpdatedQuestions(newQuestions);
  };

  const isValidQuestionnaire = () => {
    const basicValidation = (
      updatedTitle.trim() && 
      updatedDescription.trim() && 
      updatedQuestions.every(q => q.text.trim()) &&
      updatedStartDate &&
      updatedEndDate &&
      studentsPerTeam > 0 &&
      !hasDuplicateOptions
  );

  if (enableTeamAllocationFeedback) {
      return basicValidation && teamAllocationFeedbackDuration > 0;
  }
  return basicValidation;
  };

  if (loading) return <p>Loading...</p>;

  return (
    <div className="edit-container">
      <div className="content-wrapper">
        <Button
            variant="outlined"
            onClick={() => navigate(-1)}    
        >
            Back
        </Button>

        {questionnaire ? (
          <>
            <Stepper activeStep={step} alternativeLabel>
              {["Edit Questions", "Preview", "Save"].map((label, index) => (
                <Step key={index} onClick={() => setStep(index)}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            <div className="step-content">
              {step === 0 && (
                <>
                  <TextField
                    fullWidth
                    label="Title"
                    value={updatedTitle}
                    onChange={(e) => setUpdatedTitle(e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    value={updatedDescription}
                    onChange={(e) => setUpdatedDescription(e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Start Date and Time"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={updatedStartDate}
                    onChange={(e) => setUpdatedStartDate(e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="End Date and Time"
                    type="datetime-local"
                    InputLabelProps={{ shrink: true }}
                    value={updatedEndDate}
                    onChange={(e) => setUpdatedEndDate(e.target.value)}
                    margin="normal"
                  />
                  <TextField
                    fullWidth
                    label="Students Per Team"
                    type="number"
                    value={studentsPerTeam}
                    onChange={(e) => setStudentsPerTeam(Number(e.target.value))}
                    margin="normal"
                    inputProps={{ min: 1 }}
                  />
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={enableFeedback}
                      onChange={(e) => setEnableFeedback(e.target.checked)}
                      id="enableFeedback"
                    />
                    <label htmlFor="enableFeedback">
                      Enable Questionnaire Feedback (Will be available until the questionnaire has ended)
                    </label>
                  </div>
                  <div className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={enableTeamAllocationFeedback}
                      onChange={(e) => setEnableTeamAllocationFeedback(e.target.checked)}
                      id="enableTeamFeedback"
                    />
                    <label htmlFor="enableTeamFeedback">
                      Enable Team Allocation Feedback
                    </label>
                  </div>
                  {enableTeamAllocationFeedback && (
                    <div className="feedback-duration-container">
                      <TextField
                        fullWidth
                        label="Team Allocation Feedback Duration (number of days after questionnaire has ended)"
                        type="number"
                        value={teamAllocationFeedbackDuration}
                        onChange={(e) => setTeamAllocationFeedbackDuration(Number(e.target.value))}
                        margin="normal"
                        inputProps={{ min: 1 }}
                      />
                    </div>
                  )}
                  <h3>Questions</h3>
                  {updatedQuestions.map((q, qIndex) => (
                    <div key={qIndex} className="question-card">
                      <TextField
                        fullWidth
                        label={`Question ${qIndex + 1}`}
                        value={q.text}
                        onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
                        margin="normal"
                      />

                      <p><strong>Weight Function:</strong></p>
                      <RadioGroup
                        value={q.WeightFun}
                        onChange={(e) => handleWeightFunChange(qIndex, e.target.value)}
                        row
                      >
                        <FormControlLabel value="group" control={<Radio />} label="Group" />
                        <FormControlLabel value="balance" control={<Radio />} label="Balance" />
                      </RadioGroup>

                      {q.options.map((option, optIndex) => {
                        const isDuplicate = updatedQuestions.some(q => {
                            const nonEmptyOptions = q.options.filter(opt => opt.trim() !== "");
                            const uniqueOptions = new Set(nonEmptyOptions.map(opt => opt.trim().toLowerCase()));
                            return nonEmptyOptions.length !== uniqueOptions.size;
                        }) && 
                        q.options.some((opt, idx) => 
                            idx !== optIndex && 
                            opt.trim().toLowerCase() === option.trim().toLowerCase() && 
                            option.trim() !== ""
                        );

                        return (
                            <div key={optIndex} className="option-row">
                                <TextField
                                    label={`Option ${optIndex + 1}`}
                                    value={option}
                                    onChange={(e) => handleOptionChange(qIndex, optIndex, e.target.value)}
                                    variant="outlined"
                                    size="small"
                                    error={isDuplicate}
                                    helperText={isDuplicate ? "Duplicate option" : ""}
                                    className="option-textfield"
                                />
                                <IconButton
                                  onClick={() => deleteOption(qIndex, optIndex)}
                                  color="error"
                                  size="small"
                                  style={{ marginLeft: '8px' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                            </div>
                        );
                      })}
                      <Button startIcon={<AddCircleIcon />} onClick={() => addOption(qIndex)}>
                        Add Option
                      </Button>
                      <IconButton onClick={() => deleteQuestion(qIndex)} style={{ marginLeft: "10px" }}>
                          <DeleteIcon />
                      </IconButton>
                    </div>
                  ))}
                  <Button variant="contained" onClick={addQuestion} className="add-question-button">
                      Add Question
                  </Button>
                </>
              )}

              {step === 1 && (
                <div className="preview-section">
                  <h3 className="preview-header">Preview</h3>
                  <div className="preview-metadata">
                    <h4 className="preview-title">{updatedTitle}</h4>
                    <p className="preview-description">{updatedDescription}</p>

                    <div className="metadata-grid">
                      <div className="metadata-item">
                        <span className="metadata-label">Start Date:</span>
                        <span className="metadata-value">{new Date(updatedStartDate).toLocaleString()}</span>
                      </div>
                      <div className="metadata-item">
                        <span className="metadata-label">End Date:</span>
                        <span className="metadata-value">{new Date(updatedEndDate).toLocaleString()}</span>
                      </div>
                      <div className="metadata-item">
                        <span className="metadata-label">Students Per Team:</span>
                        <span className="metadata-value">{studentsPerTeam}</span>
                      </div>
                      <div className="metadata-item">
                        <span className="metadata-label">Questionnaire Feedback:</span>
                        <span className="metadata-value">{enableFeedback ? "Enabled" : "Disabled"}</span>
                      </div>
                      {enableTeamAllocationFeedback && (
                        <>
                          <div className="metadata-item">
                            <span className="metadata-label">Team Feedback Duration:</span>
                            <span className="metadata-value">{teamAllocationFeedbackDuration} days</span>
                          </div>
                          <div className="metadata-item">
                            <span className="metadata-label">Team Allocation Feedback Period:</span>
                            <span className="metadata-value">
                              {new Date(updatedEndDate).toLocaleString()} to<br />
                              {new Date(new Date(updatedEndDate).getTime() + teamAllocationFeedbackDuration * 24 * 60 * 60 * 1000).toLocaleString()}
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="questions-container">
                    {updatedQuestions.map((q, index) => (
                      <div key={index} className="question-preview-card">
                        <div className="question-preview-header">
                          <h5 className="question-preview-text">{q.text}</h5>
                          <span className={`weight-tag weight-${q.WeightFun}`}>
                            {q.WeightFun}
                          </span>
                        </div>
                        <ul className="options-list">
                          {q.options.map((opt, idx) => opt && (
                            <li key={idx} className="option-preview-item">
                              <span className="option-bullet"></span>
                              <span className="option-preview-text">{opt}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="confirmation-message">
                  <h3>Are you sure you want to save the changes?</h3>
                  <p>Click "Save Questionnaire" to confirm.</p>
                  <Button variant="contained" disabled={!isValidQuestionnaire()} onClick={handleSave}>
                    Save Questionnaire
                  </Button>
                </div>
              )}

              <div className="navigation-buttons">
                {error && <p className="error-message">{error}</p>}
                {step > 0 && <Button onClick={handleBack}>Back</Button>}
                {step < 2 && <Button onClick={handleNext}>Next</Button>}
              </div>
            </div>
          </>
        ) : (
          <p>Questionnaire not found.</p>
        )}
      </div>
    </div>
  );
}

export default EditQuestionnaire;